import React, { useState } from 'react'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
  getBigZero,
  isValidInputDecimal,
} from '../utils/bigJSHelper'
import { amountFromInput } from '../utils/amountFromInput'
export interface UseCalculate {
  result: string
  inputValues: {
    [key: string]: string
  }
  changeValueForCalculate: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  onBlurValue: (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  resetCalculatedAmount?: () => void
  updateResult?: (value: string) => void
  updateInputValues?: (value: { [key: string]: string }) => void
}

/**
 * Handles the change in input values.
 *
 * Validates the input, updates the input values and recalculates the result.
 *
 * Input change management
 *
 * 1- add the function changeValueForCalculate to the onChange function
 * (this allows us to control the input changes).
 *
 * 2- add inputValues to the value property of the input (keep in mind
 * that with the name of the input you will access to the value)
 *
 * Shows the value of the sum obtained
 *
 * use the `result` property in the field in which you want to display the result
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import { useCalculateTotalAmount } from './useCalculateTotalAmount';
 *
 * // Example component
 * const SumInputs = () => {
 * const { inputValues, changeValueForCalculate, result } =
 *  useCalculateTotalAmount()
 *   return (
 *     <div>
 *       <input
 *         name="input1"
 *         value={inputValues['input1'] ?? ''}
 *         onChange={changeValueForCalculate}
 *       />
 *       <input
 *         name="input2"
 *         value={inputValues['input2'] ?? ''}
 *         onChange={changeValueForCalculate}
 *       />
 *       <input
 *         name="result"
 *         value={result}
 *       />
 *     </div>
 *   );
 * };
 *
 * export default SumInputs;
 * ```
 */
export const useCalculateTotalAmount = (): UseCalculate => {
  const [result, setResult] = useState('')
  const [inputValues, setInputValues] = useState<{
    [key: string]: string
  }>({})

  const onBlurValue = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = event.target
    setInputValues((prevInputValue) => {
      const updatedInputValues = {
        ...prevInputValue,
        [name]: amountFromInput(value),
      }
      return updatedInputValues
    })
  }
  const changeValueForCalculate = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = event.target
    if (!isValidInputDecimal(value)) return
    setInputValues((prevInputValue) => {
      const updatedInputValues = {
        ...prevInputValue,
        [name]: value,
      }

      // addition operations
      let newResult = getBigZero()
      for (const key in updatedInputValues) {
        const bigValue = convertTwoDecimalStringToBig(
          // prevents the entry of unsupported values such as letters or spacing
          amountFromInput(updatedInputValues[key])
        )
        newResult = newResult.plus(bigValue)
      }

      setResult(newResult.eq(0) ? '' : formatBigFixedTwoDecimals(newResult))

      return updatedInputValues
    })
  }

  const resetCalculatedAmount = () => {
    setResult('')
    setInputValues({})
  }

  const updateResult = (value: string) => {
    setResult(value)
  }

  const updateInputValues = (value: { [key: string]: string }) => {
    setInputValues(value)
  }

  return {
    result,
    inputValues,
    changeValueForCalculate,
    resetCalculatedAmount,
    updateResult,
    updateInputValues,
    onBlurValue,
  }
}
